import React, { useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";

import { CompanyContextSwitcherLogo } from "@/components/User/CompanyContextSwitcherLogo";
import { ConsultantContextSwitcherAvatar } from "@/components/User/ConsultantContextSwitcherAvatar";
import { ContextSwitchModal } from "@/components/User/ContextSwitchModal";
import { IConsultantProfile } from "@/data/consultant-profiles";
import { Flex } from "@/eworkitComponents";
import { deleteSearchParam, searchParamsFromQuery } from "@/infrastructure/Route";

import { UserContext } from "./UserContext";

import "./_context-switcher-logo-section.scss";

interface IContextSwitcherProps {
  children: React.ReactElement;
}

const routeTranslationMatcher = {
  "job-requests": "REQUEST",
  "my-team": "PROFILE",
  profiles: "PROFILE",
  "job-applications": "APPLICATION",
  default: "REQUEST",
};

export const ContextSwitcher: React.FC<IContextSwitcherProps> = React.memo(props => {
  const { t, ready } = useTranslation("session");
  const User = useContext(UserContext);
  const location = useLocation();
  const history = useHistory();
  const contextProfileIdSearchParam = searchParamsFromQuery(location.search).get("contextProfileId");
  const contextProfileId = contextProfileIdSearchParam && parseInt(contextProfileIdSearchParam, 10);
  const [contextModalOpen, setContextModalOpen] = useState(true);

  const baseClassName = "context-switcher-logo-section";

  const renderInitialContext = useCallback(() => {
    return (
      <Flex direction="row" justify="space-between">
        <Flex
          direction="column"
          className={`${baseClassName}__logo-container`}
          justify="center"
          align="center"
          margin={["sm", false]}>
          {User.contextCompany ? (
            <CompanyContextSwitcherLogo company={User.contextCompany} />
          ) : (
            User.contextConsultantProfile && (
              <ConsultantContextSwitcherAvatar consultant={User.contextConsultantProfile} />
            )
          )}
        </Flex>
      </Flex>
    );
  }, []);

  const renderTargetContext = useCallback((targetProfile: IConsultantProfile) => {
    return (
      <Flex direction="row" justify="space-between">
        <Flex
          direction="column"
          className={`${baseClassName}__logo-container`}
          justify="center"
          align="center"
          margin={["sm", false]}>
          {targetProfile.sharedProfile?.company ? (
            <CompanyContextSwitcherLogo company={targetProfile.sharedProfile.company} />
          ) : (
            <ConsultantContextSwitcherAvatar consultant={targetProfile} />
          )}
        </Flex>
      </Flex>
    );
  }, []);

  if (!contextProfileId || (User.contextCompany && contextProfileId === User.contextConsultantProfile?.id)) {
    return props.children;
  }
  const profile = User.sessionUser.consultantProfiles.find((c: IConsultantProfile) => c.id === contextProfileId);

  if (!profile) {
    return props.children;
  }

  const handleAcceptChange = () => {
    User.setConsultantProfileContext(profile.id);
    history.push(`${location.pathname}?${deleteSearchParam(location.search, "contextProfileId")}`);
  };

  const handleCancel = () => {
    setContextModalOpen(false);
    history.push(`${location.pathname}?${deleteSearchParam(location.search, "contextProfileId")}`);
  };

  const matchingRouteTranslation = Object.entries(routeTranslationMatcher).find(
    ([key]) => location.pathname.indexOf(key) !== -1,
  );
  const translationKey = matchingRouteTranslation ? matchingRouteTranslation[1] : routeTranslationMatcher.default;
  return (
    <>
      {ready && (
        <>
          <ContextSwitchModal
            onAccept={handleAcceptChange}
            acceptButtonLabel={t(`USERS.CONTEXT_SWITCHING.OPEN_${translationKey}`)}
            cancelButtonLabel={t("USERS.CONTEXT_SWITCHING.CANCEL_BUTTON")}
            fromContext={renderInitialContext()}
            toContext={renderTargetContext(profile)}
            open={contextModalOpen}
            onClose={handleCancel}
          />
          {props.children}
        </>
      )}
    </>
  );
});
